import {useState, type SyntheticEvent} from 'react'
import {Link as RouterLink} from 'react-router-dom'
import {Trans, useTranslation} from 'react-i18next'
import {submit as submitContactForm} from '../../common/api/generated/endpoints/contact/contact'
import type {ContactTopic} from '../../common/api/generated/model/contactTopic'
import {executeRecaptcha} from '../utils/recaptcha'
import {trackEvent} from '../../common/analytics/trackEvent'
import './ContactPage.css'

type IconName = 'pin' | 'send' | 'check'

const TOPICS: readonly ContactTopic[] = ['general', 'bug', 'billing']

const ContactIcon = ({name, size = 16}: {name: IconName; size?: number}) => {
  const common = {
    width: size,
    height: size,
    viewBox: '0 0 24 24',
    fill: 'none' as const,
    stroke: 'currentColor',
    strokeWidth: 1.7,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
  }
  switch (name) {
    case 'pin':
      return (
        <svg {...common}>
          <path d="M12 22s7-6.5 7-12a7 7 0 0 0-14 0c0 5.5 7 12 7 12z" />
          <circle cx="12" cy="10" r="2.5" />
        </svg>
      )
    case 'send':
      return (
        <svg {...common}>
          <path d="M22 2L11 13" />
          <path d="M22 2l-7 20-4-9-9-4 20-7z" />
        </svg>
      )
    case 'check':
      return (
        <svg {...common}>
          <path d="M5 12l5 5L20 7" />
        </svg>
      )
  }
}

interface FormState {
  name: string
  email: string
  topic: ContactTopic
  subject: string
  message: string
}

const initialForm: FormState = {
  name: '',
  email: '',
  topic: 'general',
  subject: '',
  message: '',
}

const ContactPage = () => {
  const {t} = useTranslation()
  const [form, setForm] = useState<FormState>(initialForm)
  const [submitting, setSubmitting] = useState(false)
  const [sent, setSent] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleChange =
    <K extends keyof FormState>(key: K) =>
    (e: {target: {value: string}}) => {
      setForm((prev) => ({...prev, [key]: e.target.value as FormState[K]}))
    }

  const handleSubmit = async (e: SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (submitting) return
    setError(null)
    setSubmitting(true)
    trackEvent('contact_form_submitted', {topic: form.topic})
    try {
      const recaptchaToken = await executeRecaptcha('contact')
      await submitContactForm({...form, recaptchaToken})
      setSent(true)
      trackEvent('contact_form_success', {topic: form.topic})
    } catch {
      setError(t('contact.form.error'))
      trackEvent('contact_form_failed', {topic: form.topic})
    } finally {
      setSubmitting(false)
    }
  }

  const handleSendAnother = () => {
    setSent(false)
    setError(null)
    setForm(initialForm)
  }

  return (
    <div className="contact-root">
      <div className="cn-grid" aria-hidden />

      <main className="contact-page">
        <header className="cn-hero">
          <span className="cn-eyebrow">
            <span className="cn-eyebrow-dot" />
            {t('contact.eyebrow')}
          </span>
          <h1 className="cn-title">
            {t('contact.titleLead')} <em>{t('contact.titleEm')}</em>
          </h1>
          <p className="cn-lede">{t('contact.lede')}</p>
        </header>

        <div className="cn-grid-2col">
          {sent ? (
            <div className="cn-success" role="status" aria-live="polite">
              <span className="cn-success-icon">
                <ContactIcon name="check" size={20} />
              </span>
              <h2 className="cn-success-title">{t('contact.sentTitle')}</h2>
              <p className="cn-success-body">{t('contact.sentBody')}</p>
              <button type="button" className="cn-ghost" onClick={handleSendAnother}>
                {t('contact.sendAnother')}
              </button>
            </div>
          ) : (
            <form className="cn-form" onSubmit={handleSubmit} noValidate>
              <div className="cn-row">
                <div className="cn-field">
                  <label htmlFor="c-name" className="cn-label">
                    {t('contact.form.labelName')}
                  </label>
                  <input
                    id="c-name"
                    className="cn-input"
                    type="text"
                    autoComplete="name"
                    placeholder={t('contact.form.placeholderName')}
                    value={form.name}
                    onChange={handleChange('name')}
                    required
                    maxLength={120}
                  />
                </div>
                <div className="cn-field">
                  <label htmlFor="c-email" className="cn-label">
                    {t('contact.form.labelEmail')}
                  </label>
                  <input
                    id="c-email"
                    className="cn-input"
                    type="email"
                    autoComplete="email"
                    placeholder={t('contact.form.placeholderEmail')}
                    value={form.email}
                    onChange={handleChange('email')}
                    required
                  />
                </div>
              </div>

              <div className="cn-field">
                <label htmlFor="c-topic" className="cn-label">
                  {t('contact.form.labelTopic')}
                </label>
                <select id="c-topic" className="cn-select" value={form.topic} onChange={handleChange('topic')}>
                  {TOPICS.map((topic) => (
                    <option key={topic} value={topic}>
                      {t(`contact.form.topics.${topic}`)}
                    </option>
                  ))}
                </select>
              </div>

              <div className="cn-field">
                <label htmlFor="c-subject" className="cn-label">
                  {t('contact.form.labelSubject')}
                </label>
                <input
                  id="c-subject"
                  className="cn-input"
                  type="text"
                  placeholder={t('contact.form.placeholderSubject')}
                  value={form.subject}
                  onChange={handleChange('subject')}
                  required
                  maxLength={200}
                />
              </div>

              <div className="cn-field">
                <label htmlFor="c-msg" className="cn-label">
                  {t('contact.form.labelMessage')}
                </label>
                <textarea
                  id="c-msg"
                  className="cn-textarea"
                  placeholder={t('contact.form.placeholderMessage')}
                  value={form.message}
                  onChange={handleChange('message')}
                  required
                  maxLength={5000}
                />
              </div>

              {error && <p className="cn-error">{error}</p>}

              <button type="submit" className="cn-submit" disabled={submitting}>
                <ContactIcon name="send" size={14} />
                {submitting ? t('contact.form.sending') : t('contact.form.submit')}
              </button>

              <p className="cn-fine">
                <Trans i18nKey="contact.form.fine" components={{privacy: <RouterLink to="/privacy" />}} />
              </p>
            </form>
          )}

          <aside className="cn-side">
            <div className="cn-card">
              <span className="cn-card-icon">
                <ContactIcon name="pin" size={18} />
              </span>
              <div>
                <h3 className="cn-card-title">{t('contact.cards.addressTitle')}</h3>
                <p className="cn-card-body">
                  <Trans i18nKey="contact.cards.addressBody" components={{br: <br />}} />
                </p>
              </div>
            </div>
          </aside>
        </div>
      </main>
    </div>
  )
}

export default ContactPage
